// SupplierPurchaseOrderInward.jsx
import React, { Component } from "react";
import Fuse from "fuse.js";
import CartContext from "./CartContext";
import { MessageBox } from "./MessageBox";
import {
  Navbar,
  Nav,
  Button,
  Row,
  Col,
  Accordion,
  Table,
  FormControl,
  Stack,
  ButtonToolbar,
  Form,
  Badge,
  Spinner,
} from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";

export class SupplierPurchaseOrderInward extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      items: [], // flat rows from API
      filteredItems: [],
      searchQuery: "",
      orderNoFilter: "",
      suppliers: [],
      selectedSupplierId: "",
      selectedPaymentStatus: "all", // "all" | "1" | "2" | "3"
      lastFetchedAt: null,
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
    };
  }

  // helper to determine if current user is a supplier and their supplier id
  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser =
      user && roles && user.roleId === roles.SUPPLIER ? true : false;
    const supplierUserId = user ? String(user.storeRoleValue || "") : "";
    return { isSupplierUser, supplierUserId };
  }

  componentDidMount() {
    // load suppliers then set selectedSupplierId for supplier user (if any), then fetch
    this.loadSuppliers()
      .then((list) => {
        const { isSupplierUser, supplierUserId } =
          this.getSupplierUserContext();
        if (isSupplierUser && supplierUserId) {
          this.setState({ selectedSupplierId: supplierUserId }, () =>
            this.fetchItems()
          );
        } else {
          this.fetchItems();
        }
      })
      .catch(() => {
        // still attempt to fetch even if loadSuppliers failed
        this.fetchItems();
      });
  }

  getStoreId() {
    return this.props.storeId || this.context?.storeId || "";
  }

  loadSuppliers = async () => {
    const storeId = this.getStoreId();
    if (!storeId) return [];
    try {
      const r = await fetch(
        `${process.env.REACT_APP_API}Supplier/GetSuppliers/${storeId}`,
        { credentials: "same-origin" }
      );
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      this.setState({ suppliers: list });
      return list;
    } catch (err) {
      console.error("loadSuppliers error", err);
      this.setState({ suppliers: [] });
      return [];
    }
  };

  // Build API url (flat endpoint)
  buildUrl() {
    const storeId = this.getStoreId();
    if (!storeId) return null;
    const base = process.env.REACT_APP_API || "";
    const { selectedSupplierId } = this.state;
    if (selectedSupplierId && selectedSupplierId.trim() !== "") {
      return `${base}SupplierPurchaseOrderDelivered/bysupplier/${storeId}/${selectedSupplierId}/flat`;
    }
    return `${base}SupplierPurchaseOrderDelivered/flat/${storeId}`;
  }

  // improved fetchItems
  fetchItems = async () => {
    const url = this.buildUrl();
    if (!url) {
      this.ShowMessagBox(
        "Error",
        "storeId is required to load delivered orders."
      );
      console.error("fetchItems aborted: no storeId / buildUrl returned null");
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const resp = await fetch(url, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => "<no body>");
        const contentType = resp.headers.get("content-type") || "";
        console.error("fetchItems non-ok response", {
          url,
          status: resp.status,
          statusText: resp.statusText,
          contentType,
          body: text,
        });
        let parsed = text;
        if (contentType.includes("application/json")) {
          try {
            parsed = JSON.parse(text);
          } catch {}
        }
        this.ShowMessagBox(
          "Error loading delivered orders",
          `Server returned ${resp.status} ${resp.statusText}: ${
            typeof parsed === "string" ? parsed : JSON.stringify(parsed)
          }`
        );
        this.setState({
          loading: false,
          error: `HTTP ${resp.status} ${resp.statusText}`,
        });
        return;
      }

      const contentType = resp.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const txt = await resp.text().catch(() => "<no body>");
        console.error("fetchItems unexpected content-type", {
          url,
          contentType,
          body: txt,
        });
        this.ShowMessagBox("Error", `Expected JSON but got ${contentType}`);
        this.setState({ loading: false, error: "Invalid content-type" });
        return;
      }

      const data = await resp.json().catch(async (err) => {
        const txt = await resp.text().catch(() => "<no body>");
        console.error("fetchItems json parse failed", { url, err, body: txt });
        this.ShowMessagBox("Error", "Failed to parse server response as JSON");
        throw err;
      });

      const list = Array.isArray(data) ? data : [];
      const normalized = list.map((i) => {
        const rate =
          Number(
            i.rate ??
              i.Rate ??
              i.mrp ??
              i.Mrp ??
              i.purchasePrice ??
              i.PurchasePrice ??
              0
          ) || 0;
        const qty = Number(i.qty ?? i.Qty ?? 0) || 0;
        const amount = qty * rate;
        return {
          orderId: i.orderId ?? i.OrderId ?? null,
          invoiceDate:
            i.invoiceDate ??
            i.InvoiceDate ??
            i.orderDate ??
            i.OrderDate ??
            null,
          orderNo: i.orderNo ?? i.OrderNo ?? i.invoiceNo ?? i.InvoiceNo ?? "",
          itemId: i.itemId ?? i.ItemId ?? null,
          itemName: i.itemName ?? i.ItemName ?? "",
          qty: qty,
          supplierId: i.supplierId ?? i.SupplierId ?? null,
          storeId: i.storeId ?? i.StoreId ?? null,
          addedToStock: i.addedToStock ?? i.AddedToStock ?? false,
          batchId: i.batchId ?? i.BatchId ?? "",
          hsnCode: i.hsnCode ?? i.HsnCode ?? "",
          remarks: i.remarks ?? i.Remarks ?? "",
          paymentStatusId: i.paymentStatusId ?? i.PaymentStatusId ?? 0,
          rate: rate,
          amount: amount,
          totalAmount:
            Number(i.totalAmount ?? i.TotalAmount ?? i.total ?? i.Total ?? 0) ||
            0,
          paidAmount:
            Number(i.paidAmount ?? i.PaidAmount ?? i.paid ?? i.Paid ?? 0) || 0,
        };
      });

      normalized.sort((a, b) => {
        const da = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
        const db = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
        if (da !== db) return db - da;
        return (b.orderNo || "").localeCompare(a.orderNo || "");
      });

      this.setState(
        { items: normalized, lastFetchedAt: new Date(), loading: false },
        () => this.applyFilters()
      );
    } catch (err) {
      console.error("fetchItems exception", err);
      this.ShowMessagBox(
        "Error",
        `Failed to load delivered orders: ${
          err && err.message ? err.message : String(err)
        }`
      );
      this.setState({
        loading: false,
        error: err && err.message ? err.message : String(err),
      });
    }
  };

  // refresh
  handleRefresh = () => this.fetchItems();

  handleSearchChange = (e) =>
    this.setState({ searchQuery: e.target.value || "" }, () =>
      this.applyFilters()
    );

  handleOrderNoFilterChange = (e) =>
    this.setState({ orderNoFilter: e.target.value || "" }, () =>
      this.applyFilters()
    );

  handleSupplierFilterChange = (e) => {
    const { isSupplierUser } = this.getSupplierUserContext();
    if (isSupplierUser) return; // ignore changes if user is supplier
    const val = e.target.value || "";
    this.setState(
      { selectedSupplierId: val, items: [], filteredItems: [] },
      () => this.fetchItems()
    );
  };

  handlePaymentStatusTab = (key) =>
    this.setState({ selectedPaymentStatus: key }, () => this.applyFilters());

  applyFilters = () => {
    let results = (this.state.items || []).slice();
    const { selectedPaymentStatus, orderNoFilter, searchQuery } = this.state;

    // If logged-in supplier, restrict to their supplier id (defense-in-depth).
    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    if (isSupplierUser && supplierUserId) {
      results = results.filter(
        (it) =>
          (it.supplierId || "") &&
          String(it.supplierId).toLowerCase() ===
            String(supplierUserId).toLowerCase()
      );
    }

    // Filter by paymentStatus
    if (selectedPaymentStatus && selectedPaymentStatus !== "all") {
      const num = Number(selectedPaymentStatus);
      results = results.filter((it) => Number(it.paymentStatusId ?? 0) === num);
    }

    // orderNo filter
    if (orderNoFilter && orderNoFilter.trim() !== "") {
      const q = orderNoFilter.trim().toLowerCase();
      results = results.filter((it) =>
        (it.orderNo || "").toLowerCase().includes(q)
      );
    }

    // fuzzy search across fields
    if (searchQuery && searchQuery.trim() !== "") {
      try {
        const fuse = new Fuse(results, {
          keys: [
            "orderNo",
            "itemName",
            "batchId",
            "hsnCode",
            "remarks",
            "supplierId",
          ],
          threshold: 0.25,
          ignoreLocation: true,
        });
        results = fuse.search(searchQuery).map((r) => r.item);
      } catch {
        const q = searchQuery.trim().toLowerCase();
        results = results.filter((it) =>
          (
            (it.orderNo || "") +
            " " +
            (it.itemName || "") +
            " " +
            (it.batchId || "") +
            " " +
            (it.hsnCode || "") +
            " " +
            (it.remarks || "")
          )
            .toLowerCase()
            .includes(q)
        );
      }
    }

    // sort again
    results.sort((a, b) => {
      const da = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
      const db = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
      if (da !== db) return db - da;
      return (b.orderNo || "").localeCompare(a.orderNo || "");
    });

    this.setState({ filteredItems: results });
  };

  // supplier name lookup
  getSupplierLabel = (supplierId) => {
    if (!supplierId) return "";
    const idStr = String(supplierId).toLowerCase();
    const s = (this.state.suppliers || []).find(
      (x) => (x.id || "").toString().toLowerCase() === idStr
    );
    if (!s) return supplierId;
    return s.name || s.companyName || s.emailId || s.id;
  };

  paymentStatusBadge = (statusId) => {
    switch (Number(statusId)) {
      case 1:
        return <Badge bg="success">Paid</Badge>;
      case 2:
        return <Badge bg="danger">Unpaid</Badge>;
      case 3:
        return (
          <Badge bg="warning" text="dark">
            Partial
          </Badge>
        );
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  // compute counts for paymentStatus across orders (grouped by orderId)
  paymentStatusCounts = () => {
    const counts = { all: 0, 1: 0, 2: 0, 3: 0 };
    const byOrder = this.groupByOrder(this.state.items || []);
    Object.values(byOrder).forEach((group) => {
      counts.all += 1;
      const ps = Number(group.meta.paymentStatusId ?? 0);
      if (counts[ps] == null) counts[ps] = 0;
      counts[ps] += 1;
    });
    return counts;
  };

  // Group flat items into orders keyed by orderId (or orderNo fallback)
  groupByOrder = (items) => {
    const map = {};
    items.forEach((it) => {
      const key = it.orderId ?? it.orderNo ?? "__unknown__";
      if (!map[key]) {
        map[key] = {
          meta: {
            orderId: it.orderId,
            orderNo: it.orderNo,
            invoiceDate: it.invoiceDate,
            supplierId: it.supplierId,
            storeId: it.storeId,
            paymentStatusId: it.paymentStatusId ?? 0,
            totalAmount: it.totalAmount ?? null,
            paidAmount: it.paidAmount ?? null,
          },
          items: [],
        };
      }
      map[key].items.push(it);
    });

    Object.keys(map).forEach((k) => {
      const group = map[k];
      if (
        group.meta.totalAmount == null ||
        Number(group.meta.totalAmount) === 0
      ) {
        const total = group.items.reduce(
          (s, it) => s + Number(it.amount || 0),
          0
        );
        group.meta.totalAmount = total;
      }
      if (group.meta.paidAmount == null) {
        const anyPaid = group.items.find(
          (it) => it.paidAmount && Number(it.paidAmount) > 0
        );
        group.meta.paidAmount = anyPaid ? Number(anyPaid.paidAmount) : 0;
      }
    });

    return map;
  };

  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });

  // Renders accordion header summary for a group
  renderAccordionHeader = (meta, items) => {
    const dt = meta.invoiceDate ? new Date(meta.invoiceDate) : null;
    const invoiceStr = dt
      ? dt.toLocaleDateString() + " " + dt.toLocaleTimeString()
      : "";
    const totalQty = items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
    const itemCount = items.length;

    const locale =
      (this.context &&
        this.context.storeSettings &&
        this.context.storeSettings.defaultLocale) ||
      undefined;
    const currency =
      (this.context &&
        this.context.storeSettings &&
        this.context.storeSettings.defaultCurrency) ||
      undefined;

    const fmt = (value) => {
      const num = Number(value || 0);
      try {
        if (locale && currency) {
          return num.toLocaleString(locale, {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
          });
        } else if (currency) {
          return num.toLocaleString(undefined, {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
          });
        } else {
          return num.toLocaleString();
        }
      } catch {
        return num.toFixed(2);
      }
    };

    const totalAmount = Number(meta.totalAmount || 0);
    const paidAmount = Number(meta.paidAmount || 0);
    const balance = totalAmount - paidAmount;

    return (
      <div className="d-flex align-items-center justify-content-between w-100">
        <Row className="w-100">
          <Col md={2}>
            <strong>Invoice No: {meta.orderNo || "(No InvoiceNo)"}</strong>
          </Col>
          <Col md={2}>
            <div className="small text-muted">Invoice Date: {invoiceStr}</div>
          </Col>
          <Col md={2}>Supplier: {this.getSupplierLabel(meta.supplierId)} </Col>
          <Col md={6}>
            <div className="text-end small">
              <div style={{ fontWeight: 600 }}>
                Total: {fmt(totalAmount)} &nbsp;&middot;&nbsp; Paid:{" "}
                {fmt(paidAmount)}
                &nbsp;&middot;&nbsp; Balance: {fmt(balance)}
              </div>
              <div className="mt-1 text-muted">
                {itemCount} item{itemCount !== 1 ? "s" : ""} • {totalQty} qty
                &nbsp; &nbsp; {this.paymentStatusBadge(meta.paymentStatusId)}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // Render items table inside accordion body
  renderItemsTable = (items) => {
    const locale =
      (this.context &&
        this.context.storeSettings &&
        this.context.storeSettings.defaultLocale) ||
      undefined;
    const currency =
      (this.context &&
        this.context.storeSettings &&
        this.context.storeSettings.defaultCurrency) ||
      undefined;
    const fmt = (v) => {
      try {
        if (locale && currency)
          return Number(v).toLocaleString(locale, {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
          });
        if (currency)
          return Number(v).toLocaleString(undefined, {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
          });
        return Number(v).toLocaleString();
      } catch {
        return Number(v).toFixed(2);
      }
    };

    return (
      <Table striped bordered hover size="sm" className="mt-2">
        <thead>
          <tr>
            <th>Item Name</th>
            <th className="text-end">Qty</th>
            <th className="text-end">Rate</th>
            <th className="text-end">Amount</th>
            <th>Batch</th>
            <th>HSN</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it.itemId ?? idx}>
              <td>{it.itemName}</td>
              <td className="text-end">{it.qty ?? ""}</td>
              <td className="text-end">{fmt(it.rate ?? 0)}</td>
              <td className="text-end">{fmt(it.amount ?? 0)}</td>
              <td>{it.batchId}</td>
              <td>{it.hsnCode}</td>
              <td
                style={{
                  maxWidth: 300,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span title={it.remarks}>{it.remarks}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  render() {
    const {
      searchQuery,
      orderNoFilter,
      lastFetchedAt,
      loading,
      suppliers,
      selectedSupplierId,
      items,
      filteredItems,
      selectedPaymentStatus,
    } = this.state;

    const storeId = this.getStoreId();
    // Group filteredItems into orders for accordion display
    const grouped = this.groupByOrder(filteredItems || []);
    const groupKeys = Object.keys(grouped);
    const counts = this.paymentStatusCounts();

    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();

    return (
      <>
        {this.state.msgboxOpen && (
          <MessageBox
            open={this.state.msgboxOpen}
            title={this.state.msgboxTitle}
            message={this.state.msgboxMessage}
            callback={() => this.setState({ msgboxOpen: false })}
          />
        )}

        <Navbar className="shadow position-sticky fixed-top py-3 text-black">
          <h3>
            <b>Purchase Orders(PO) Delivered to Client</b>
          </h3>
          <Navbar.Collapse className="justify-content-end mx-0">
            <Nav>
              <ButtonToolbar>
                <Button
                  size="sm"
                  className="mainthemecolor"
                  onClick={this.handleRefresh}
                  disabled={loading || !storeId}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" /> Refreshing...
                    </>
                  ) : (
                    <>
                      <BiRefresh />
                      &nbsp;Refresh
                    </>
                  )}
                </Button>
              </ButtonToolbar>
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <div className="p-3" style={{ marginTop: 10 }}>
          <Stack direction="horizontal" gap={3} className="mb-2">
            <FormControl
              style={{ maxWidth: 220 }}
              type="search"
              placeholder="Filter by Invoice No..."
              value={orderNoFilter}
              onChange={this.handleOrderNoFilterChange}
            />

            <Form.Select
              style={{ maxWidth: 280 }}
              value={selectedSupplierId}
              onChange={this.handleSupplierFilterChange}
              disabled={isSupplierUser}
            >
              <option value="">— All Suppliers —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.companyName || s.emailId || s.id}
                </option>
              ))}
            </Form.Select>
          </Stack>

          {/* Payment status tabs */}
          <Nav
            variant="tabs"
            activeKey={selectedPaymentStatus}
            onSelect={this.handlePaymentStatusTab}
            className="mb-2"
          >
            <Nav.Item>
              <Nav.Link eventKey="all">
                All Orders{" "}
                <Badge bg="secondary" className="ms-1">
                  {counts.all}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="1">
                Paid Orders{" "}
                <Badge bg="success" className="ms-1">
                  {counts[1] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="2">
                Unpaid Orders{" "}
                <Badge bg="danger" className="ms-1">
                  {counts[2] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="3">
                Partially Paid Orders{" "}
                <Badge bg="warning" className="ms-1">
                  {counts[3] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Accordion grouped by Invoice/Order */}
          <Accordion defaultActiveKey={groupKeys.length ? groupKeys[0] : null}>
            {groupKeys.length === 0 && (
              <div className="text-center text-muted py-4">
                No orders found.
              </div>
            )}

            {groupKeys.map((key) => {
              const g = grouped[key];
              const meta = g.meta;
              const itemsForOrder = g.items;
              return (
                <Accordion.Item eventKey={key} key={key}>
                  <Accordion.Header>
                    {this.renderAccordionHeader(meta, itemsForOrder)}
                  </Accordion.Header>
                  <Accordion.Body>
                    {this.renderItemsTable(itemsForOrder)}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>

          <div className="mt-2 small text-muted">
            Showing {groupKeys.length} orders ({(filteredItems || []).length}{" "}
            items).
            {lastFetchedAt && (
              <span className="ms-2">
                Last fetched: {new Date(lastFetchedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default SupplierPurchaseOrderInward;
